import React from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, MapPin } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";
import { StarRating } from "@/components/StarRating";
import { Badge } from "@/components/ui/badge";
import { resolveImage } from "@/lib/media";
import { OfferBadge } from "@/components/OfferBadge";
import { FavoriteButton } from "@/components/FavoriteButton";
import { parseOpenStatus, businessKindLabel } from "@/lib/format";
import { advertisedBusinessPath } from "@/lib/whiteLabel";

export function BusinessCard({ business, offer, showHours = false }) {
  const { lang, t } = useApp();
  const openStatus = showHours ? parseOpenStatus(business.hours, lang) : null;
  return (
    <Link
      // Free listings open inside the city site (full Biswan Online menu, so a customer can keep
      // browsing); only a white-label business opens on its own chromeless `/b/:slug` page.
      to={advertisedBusinessPath(business)}
      data-testid={`business-card-${business.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="relative h-40 overflow-hidden bg-muted">
        {business.cover_image_url ? (
          <img
            src={resolveImage(business.cover_image_url)}
            alt={business.name}
            width={400}
            height={160}
            loading="lazy" decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : business.logo_url ? (
          <div className="flex h-full w-full items-center justify-center bg-card">
            <img
              src={resolveImage(business.logo_url)}
              alt={business.name}
              width={96}
              height={96}
              loading="lazy" decoding="async"
              data-testid={`business-logo-${business.slug}`}
              className="h-24 w-24 rounded-xl object-contain"
            />
          </div>
        ) : (
          <div className="flex h-full w-full items-center justify-center text-4xl font-semibold text-muted-foreground/40">
            {business.name.slice(0, 2).toUpperCase()}
          </div>
        )}
        {business.is_verified && (
          <Badge className="absolute left-3 top-3 gap-1 bg-[hsl(var(--primary))] text-primary-foreground">
            <BadgeCheck size={13} /> {t("common.verified")}
          </Badge>
        )}
        <FavoriteButton targetType="business" targetId={business.id} className="absolute right-3 top-3" />
        {offer && <OfferBadge offer={offer} className="absolute bottom-3 left-3" />}
        {business.plan === "premium" && (
          <span
            className="absolute bottom-3 right-3 rounded-full bg-[hsl(var(--brand-deep))] px-2.5 py-1 text-[11px] font-semibold text-white"
            data-testid={`business-premium-${business.slug}`}
          >
            ✦ {t("home.premiumBadge")}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-5">
        <h3 className="font-heading text-lg font-semibold leading-snug">{localized(lang, business, "name")}</h3>
        {businessKindLabel(t, business, lang) && (
          <p className="text-xs font-medium text-[hsl(var(--primary))]" data-testid={`business-kind-${business.slug}`}>
            {businessKindLabel(t, business, lang)}
          </p>
        )}
        <p className="line-clamp-2 text-sm text-muted-foreground">{localized(lang, business, "description")}</p>
        <div className="mt-auto flex flex-col gap-2 pt-2">
          <span className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
            <StarRating value={business.rating_avg} count={business.rating_count} />
            {openStatus && (
              <>
                <span>·</span>
                {openStatus.isOpen ? (
                  <span className="font-semibold text-[hsl(var(--primary))]">{t("home.openNow")}</span>
                ) : (
                  <span>{t("home.opensAt", { time: openStatus.opensLabel })}</span>
                )}
              </>
            )}
          </span>
          {business.address && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin size={12} /> {localized(lang, business, "address")}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
